/* eslint-disable no-unused-vars */
"use client";
import { getHSLAString } from "@aichat/aichat-lib";
import { WidgetConfig } from "@aichat/types/widget";
import {
  AssistantAvatar,
  Button,
  ExpandableChat,
  ExpandableChatBody,
  ExpandableChatFooter,
  ExpandableChatHeader,
  Typography,
} from "@aichat/ui";
import useWebWidget from "@aichat/ui/hooks/use-web-widget";
import {
  ArrowUp,
  BotMessageSquare,
  MicIcon,
  MicOffIcon,
  SquareIcon,
  X,
} from "lucide-react";

import { SubmitSurveyParams } from "@aichat/types/customer-survey.types";
import { UserConversationRoleEnum } from "@aichat/types/index";
import {
  ChatResponse,
  LiveChatState,
  MessageComponentResponse,
  MessageContent,
  SurveyComponent,
} from "@aichat/types/socket/widget.types";
import DigitalHuman from "@aichat/ui/components/ui/web-widget/digital-human";
import VoiceButton from "@aichat/ui/components/ui/web-widget/voice-button";
import VoiceUI from "@aichat/ui/components/ui/web-widget/voice-ui";
import { useIsFrameOnMobile } from "@aichat/ui/hooks/use-mobile";
import useVoice from "@aichat/ui/hooks/use-voice";
import { cn } from "@aichat/ui/lib/utils";
import { useFlags } from "flagsmith/react";
import {
  Dispatch,
  forwardRef,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import MessageLoading from "../chat/message-loading";
import { ChatInput__V2 } from "../chat__v2/_components/chat-input";
import ChatList__V2 from "../chat__v2/chat-list";
import WelcomeTheme from "../chat__v2/welcome-theme";
import AudioVisualizer from "./audio-visualizer";
import FeedbackSurvey from "./feedback-survey";
import { WebWidgetConfigContext } from "./use-web-widget-config";
import SelectLanguage from "./select-language";
import useLanguage from "@aichat/ui/hooks/use-language";
import { FlagsmithTraits } from "@aichat/types/index";
import { getFooterLink } from "@aichat/constants/utm";
import AdditionalChannels from "./additional-channels";

const SHOW_DIGITAL_HUMAN = true;

export type WebWidgetProps = {
  config: WidgetConfig;
  messages: MessageContent[];
  updateMessages: (
    updater: (messages: MessageContent[]) => MessageContent[],
  ) => void;
  customerSurvey: SurveyComponent["component"] | null;
  chat: ChatResponse | null;
  isStreaming: boolean;

  onSend: (inputText: string) => void;
  submitCustomerSurvey: (payload: SubmitSurveyParams) => Promise<void>;

  onOpenChat: (open: boolean) => void;
  onCloseWelcome?: () => void;
  socketError: string | null;
  messageLoading: boolean;
  deepgramApiKey: string;
  conversationMedium?: "audio";

  setConversationMedium: (medium?: "audio") => void;
  onInterrupt: () => void;
  isVoiceBotSpeaking: boolean;
  isRecording: boolean;

  setIsRecording: (isRecording: boolean) => void;
  showWelcomeContainer: boolean;
  voiceOverlay: MessageComponentResponse | null;
  hideVoiceOverlay: () => void;
  heygenApiKey: string;
  onAudioComplete?: () => void;

  streamedMsgContent?: MessageContent;
  onAvatarStartTalking?: () => void;
  onAvatarStopTalking?: () => void;

  endLiveChat: () => void;
  liveChatState: LiveChatState;
};

const WebWidgetProvider = ({
  children,
  config,
}: PropsWithChildren<{ config: WidgetConfig }>) => {
  return (
    <WebWidgetConfigContext.Provider value={config}>
      {children}
    </WebWidgetConfigContext.Provider>
  );
};

export default function WebWidget({
  config,
  messages,
  customerSurvey,
  submitCustomerSurvey,
  onSend,
  chat,
  isStreaming,
  onOpenChat,
  onCloseWelcome,
  onInterrupt,
  messageLoading: isLoading,
  socketError,
  deepgramApiKey,
  conversationMedium,
  setConversationMedium,
  isVoiceBotSpeaking,
  isRecording,
  setIsRecording,
  showWelcomeContainer,
  voiceOverlay,
  hideVoiceOverlay,
  heygenApiKey,
  onAudioComplete,
  streamedMsgContent,
  onAvatarStartTalking,
  onAvatarStopTalking,
  endLiveChat,
  liveChatState,
  updateMessages,
}: WebWidgetProps) {
  const {
    chatListRef,
    inputText,
    onKeyDown,
    handleSend,
    setInputText,
    inputRef,
    handleSendComponent,
    interruptAvatar,
    setInterruptAvatar,
  } = useWebWidget({
    chat: chat,
    messages: messages,
    onSend: onSend,
    isStreaming,
  });

  const { digital_human } = useFlags([], [FlagsmithTraits.DIGITAL_HUMAN]);

  const [digitalHumanVoiceMode, setDigitalHumanVoiceMode] = useState(false);
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);
  const [runningTranscript, setRunningTranscript] = useState("");

  const [open, setIsOpen] = useState(false);

  const onTranscriptChange = useCallback((transcript: string) => {
    setInputText((prevState) => `${prevState} ${transcript}`);
  }, []);

  const handleToggle = (open: boolean) => {
    window.parent.postMessage(
      {
        isWidgetOpen: open,
        type: "aichat-chatbox-launch",
        showWelcomeContainer: false,
      },
      "*",
    );
    onOpenChat(open);
    setIsOpen(open);
    if (!open) {
      setInterruptAvatar(true);
    }
  };
  const handleCloseWelcome = () => {
    window.parent.postMessage(
      {
        type: "aichat-theme-close",
      },
      "*",
    );
    onCloseWelcome?.();
  };
  const showDigitalHuman = !!digital_human;
  const brandHSLAString = getHSLAString(config.brandColor);
  const brandColorHSLALight = getHSLAString({ ...config.brandColor, a: 0.7 });
  const isFirstChat = messages.length < 2 && !showDigitalHuman;
  const footerLink = getFooterLink();

  const isVoiceMode = conversationMedium === "audio";
  const isOnMobile = useIsFrameOnMobile();
  useEffect(() => {
    setInputText("");
  }, [isVoiceMode]);

  const showCustomerSurvey =
    customerSurvey?.nps?.enabled || customerSurvey?.csat?.enabled;

  const showLiveChatEnd = [
    LiveChatState.ENDING,
    LiveChatState.SHOULD_END,
  ].includes(liveChatState);

  useEffect(() => {
    if (showLiveChatEnd) {
      chatListRef.current?.scrollBy({
        top: chatListRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [showLiveChatEnd]);

  useEffect(() => {
    if (isVoiceMode) {
      updateMessages((prevMessages) =>
        prevMessages
          .filter((msg) => msg.role !== UserConversationRoleEnum.PENDING)
          .concat(
            inputText || runningTranscript
              ? [
                  {
                    content: {
                      text: `${inputText} ${runningTranscript}`.trim(),
                    },
                    createdAt: new Date().toISOString(),
                    role: UserConversationRoleEnum.PENDING,
                  },
                ]
              : [],
          ),
      );
    }
  }, [runningTranscript, inputText, isVoiceMode]);

  return (
    <WebWidgetProvider config={config}>
      {showWelcomeContainer && (
        <WelcomeTheme
          onOpenChat={handleToggle}
          onCloseWelcome={handleCloseWelcome}
        />
      )}
      <ExpandableChat
        size={config.isThemeSleek ? "sleek" : "custom"}
        position="bottom-right"
        open={open}
        isFirstChat={isFirstChat}
        onToggle={handleToggle}
        avatarVariant={config.avatarType}
        customAvatarUrl={config.avatarUrl}
        customAvatarUrlClassName="w-full h-full"
        className="isolate"
        isVoiceMode={isVoiceMode}
      >
        <>
          {config.isThemeSleek && isFirstChat && (
            <div
              id="sleek-circle-avatar-rounded-bg"
              className={cn(
                "absolute rounded-full",
                isOnMobile ? "left-12 size-32 top-28" : "left-20 size-32",
              )}
              style={{
                border: "1px solid #FFF",
              }}
            />
          )}
          {config.isThemeSleek && !isFirstChat ? null : (
            <ExpandableChatHeader
              id="expandable-chat-header"
              className={cn(
                "flex-col text-start z-20 items-start justify-end border-0 p-6 xl:p-8 transition-all duration-300 ease-in-out",
                isFirstChat
                  ? ""
                  : "h-[60px] justify-center shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_-1px_#0000001A]",
                {
                  "rounded-3xl": config.isThemeSleek,
                },
              )}
              style={{
                background: config.isThemeSleek
                  ? `linear-gradient(to top, ${brandColorHSLALight}, ${brandHSLAString})`
                  : brandHSLAString,
              }}
            >
              {isFirstChat ? (
                <div className="flex flex-col gap-3">
                  {config.isThemeSleek ? (
                    <div
                      className={cn(
                        "absolute",
                        isOnMobile
                          ? "top-[7.5rem] left-[3.5rem]"
                          : "top-[0.5rem] left-[5.5rem]",
                      )}
                      id="sleek-first-chat-avatar"
                    >
                      <AssistantAvatar
                        variant={config.avatarType}
                        customUrlClassName="size-28"
                        className="size-28"
                        customUrl={config.avatarUrl}
                      />
                    </div>
                  ) : (
                    <AssistantAvatar
                      variant={config.avatarType}
                      customUrlClassName="size-14"
                      className="size-14"
                      customUrl={config.avatarUrl}
                    />
                  )}

                  <div
                    className={cn(
                      config.textColor === "light"
                        ? "text-white"
                        : "text-black",
                      "font-semibold",
                    )}
                  >
                    <Typography variant="h2" className="text-inherit">
                      {config.greetingHeaderTitle}
                    </Typography>
                    <Typography variant="p" className="text-inherit">
                      {config.greetingHeaderSubtitle}
                    </Typography>
                  </div>

                  <AdditionalChannels
                    channels={config.additionalChannels}
                    variant="large"
                    textColor={config.textColor === "light" ? "white" : "black"}
                  />
                </div>
              ) : (
                <div className="w-full flex items-center justify-between">
                  <Typography
                    variant="titleMedium"
                    className={cn(
                      config.textColor === "light"
                        ? "text-white"
                        : "text-black",
                      "font-semibold",
                    )}
                  >
                    {config.chatbotDisplayName}
                  </Typography>
                  <div className="mr-7">
                    <AdditionalChannels
                      channels={config.additionalChannels}
                      variant="small"
                      textColor={
                        config.textColor === "light" ? "white" : "black"
                      }
                    />
                  </div>
                </div>
              )}
            </ExpandableChatHeader>
          )}
          <ExpandableChatBody id="messages-wrapper">
            {showDigitalHuman && SHOW_DIGITAL_HUMAN && (
              <div
                className={cn(
                  "absolute z-10 ",
                  config.isThemeSleek
                    ? "h-[89%] bottom-3 right-3 rounded-3xl overflow-hidden"
                    : "top-0 left-0",
                  config.isThemeSleek
                    ? isOnMobile
                      ? "w-[96%] h-[90%] bottom-[37px] right-[8px]"
                      : "w-[90%]"
                    : "w-full h-full",
                )}
              >
                <div
                  className={cn(
                    "w-full absolute bottom-0 left-0 h-full",
                    isFirstChat ? "h-[calc(100%_-_256px)]" : "h-full",
                  )}
                >
                  <DigitalHuman
                    shouldInterruptAvatar={interruptAvatar}
                    heygenApiKey={heygenApiKey}
                    onAvatarStopTalking={onAvatarStopTalking}
                    onAvatarStartTalking={onAvatarStartTalking}
                    isMessageLoading={isLoading}
                    streamedMsgContent={streamedMsgContent}
                    setIsRecording={setIsRecording}
                    isAvatarTalking={isAvatarTalking}
                    setIsAvatarTalking={setIsAvatarTalking}
                  />
                </div>
              </div>
            )}
            <div className="relative z-20 h-full flex flex-col justify-end">
              <ChatList__V2
                className={cn(
                  "z-10 flex-1 mt-auto",
                  showDigitalHuman ? "max-h-[50%]" : "max-h-full",
                )}
                messages={messages}
                chatListRef={chatListRef}
                isStreaming={isStreaming}
                showDigitalHuman={showDigitalHuman}
                handleSendComponent={handleSendComponent}
                scrollToBottomArrowHidden={showLiveChatEnd}
              />
              {isLoading && (
                <div className="p-4">
                  <MessageLoading
                    avatarVariant={config.avatarType}
                    brandHSLAString={brandHSLAString}
                  />
                </div>
              )}
              {socketError && (
                <div className="grid place-items-center">
                  <Typography
                    variant="tiny"
                    className="text-center p-2 rounded-full bg-red-50 border border-red-500 w-fit text-destructive-400"
                  >
                    {socketError}
                  </Typography>
                </div>
              )}
              {showLiveChatEnd && (
                <div className="grid place-items-center pb-2">
                  <Button
                    variant="outline"
                    className="rounded-full bg-background-50 py-1.5 px-3 h-auto text-secondary-foreground shadow-sm"
                    onClick={endLiveChat}
                    loading={liveChatState === LiveChatState.ENDING}
                  >
                    {liveChatState !== LiveChatState.ENDING ? (
                      <BotMessageSquare strokeWidth={1} />
                    ) : null}
                    Return to AI assistant
                  </Button>
                </div>
              )}
            </div>
          </ExpandableChatBody>
          {isVoiceMode && !digitalHumanVoiceMode ? (
            <VoiceUI
              config={config}
              onClose={() => {
                setConversationMedium(undefined);
              }}
              onChange={onTranscriptChange}
              onTranscript={setRunningTranscript}
              onFinal={handleSend}
              onInterrupt={onInterrupt}
              deepgramApiKey={deepgramApiKey}
              isStreaming={isStreaming}
              isVoiceBotSpeaking={isVoiceBotSpeaking}
              isMessageLoading={isLoading}
              isRecording={isRecording}
              setIsRecording={setIsRecording}
              voiceOverlay={voiceOverlay}
              hideVoiceOverlay={hideVoiceOverlay}
            />
          ) : (
            <ExpandableChatFooter
              id="expandable-chat-footer"
              className={cn(
                "border-0 min-h-[115px] p-4 z-20 bg-transparent flex flex-col gap-[6px] relative",
                {
                  "p-[1px] min-h-[65px]": config.isThemeSleek,
                },
              )}
              style={{
                background: config.isThemeSleek
                  ? ""
                  : "linear-gradient(180deg, rgba(90, 90, 90, 0.05) 0%, rgba(90, 90, 90, 0.07) 100%)",
              }}
            >
              <div
                className={cn(
                  config.isThemeSleek && !digitalHumanVoiceMode
                    ? "shadow-lg rounded-[20px] bg-white/10 backdrop-blur-lg"
                    : "",
                )}
              >
                {digitalHumanVoiceMode ? (
                  <DigitalHumanVoiceUI
                    config={config}
                    interruptAvatar={interruptAvatar}
                    setInterruptAvatar={setInterruptAvatar}
                    brandHSLAString={brandHSLAString}
                    onChange={(transcript) => {
                      setInputText((prevState) => `${prevState} ${transcript}`);
                    }}
                    isRecording={isRecording}
                    setIsRecording={setIsRecording}
                    onFinal={handleSend}
                    onInterrupt={() => onAudioComplete?.()}
                    deepgramApiKey={deepgramApiKey}
                    onClose={() => setDigitalHumanVoiceMode(false)}
                    isAvatarTalking={isAvatarTalking}
                    isThemeSleek={config.isThemeSleek}
                  />
                ) : (
                  <TextInputUI
                    config={config}
                    onKeyDown={onKeyDown}
                    isStreaming={isStreaming}
                    setInputText={setInputText}
                    inputText={inputText}
                    handleSend={handleSend}
                    brandHSLAString={brandHSLAString}
                    setDigitalHumanVoiceMode={setDigitalHumanVoiceMode}
                    setConversationMedium={setConversationMedium}
                    ref={inputRef}
                    isAvatarTalking={isAvatarTalking}
                    setInterruptAvatar={setInterruptAvatar}
                  />
                )}
              </div>
              {!config.isThemeSleek && (
                <a
                  href={footerLink}
                  target="_blank"
                  rel="noreferrer"
                  className="w-fit m-auto"
                >
                  <Typography
                    className={cn(
                      "text-center",
                      showDigitalHuman
                        ? "text-neutral-100"
                        : "text-muted-foreground",
                    )}
                    variant="detail"
                  >
                    Powered by AiChat
                  </Typography>
                </a>
              )}
            </ExpandableChatFooter>
          )}
          {showCustomerSurvey && (
            <FeedbackSurvey
              customerSurvey={customerSurvey}
              onFinish={submitCustomerSurvey}
            />
          )}
        </>
      </ExpandableChat>
    </WebWidgetProvider>
  );
}

WebWidget.displayName = "WebWidget";

export { WebWidget };

const TextInputUI = forwardRef<
  HTMLTextAreaElement,
  {
    config: WidgetConfig;

    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    isStreaming: boolean;

    setInputText: (text: string) => void;
    inputText: string;
    handleSend: () => void;
    brandHSLAString: string;
    setDigitalHumanVoiceMode: Dispatch<SetStateAction<boolean>>;
    setConversationMedium: (medium?: "audio") => void;
    isAvatarTalking?: boolean;
    setInterruptAvatar?: Dispatch<SetStateAction<boolean>>;
  }
>((props, ref) => {
  const {
    config,
    onKeyDown,
    isStreaming,
    setInputText,
    inputText,
    handleSend,
    brandHSLAString,
    setDigitalHumanVoiceMode,
    setConversationMedium,
    isAvatarTalking,
    setInterruptAvatar,
  } = props;
  const { voice_mode, digital_human } = useFlags(
    [],
    [FlagsmithTraits.DIGITAL_HUMAN, FlagsmithTraits.VOICE_MODE],
  );
  const showDigitalHuman = !!digital_human;
  const showVoiceButton = !!voice_mode;
  return (
    <div className="flex flex-row gap-1.5 items-center z-20">
      <form className="flex flex-1 relative gap-2 rounded-full p-2">
        <ChatInput__V2
          ref={ref}
          className={cn(
            "min-h-11 pr-16 focus-visible:ring-offset-0 focus-visible:ring-transparent focus-visible:border-primary bg-background border border-secondary shadow-sm",
            inputText.length > 48
              ? "rounded-xl"
              : config.isThemeSleek
                ? "rounded-[14px]"
                : "rounded-full",
          )}
          onKeyDown={onKeyDown}
          disabled={isStreaming}
          placeholder={config.placeholderMessage}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          brandHSLAString={brandHSLAString}
        />
        <Button
          disabled={isStreaming}
          onClick={() => {
            if (isAvatarTalking) {
              setInterruptAvatar?.(true);
            } else {
              handleSend();
            }
          }}
          className="absolute top-1/2 rounded-full right-3 transform size-8 -translate-y-1/2"
          style={{
            background: brandHSLAString,
            display: config.isThemeSleek && !inputText ? "none" : "",
          }}
          size="icon"
          type="button"
        >
          {isAvatarTalking ? (
            <SquareIcon
              className="size-4"
              style={{
                color: config.textColor === "light" ? "white" : "black",
              }}
            />
          ) : (
            <ArrowUp
              className="size-4"
              style={{
                color: config.textColor === "light" ? "white" : "black",
              }}
            />
          )}
        </Button>
      </form>
      {(showVoiceButton || showDigitalHuman) && (
        <VoiceButton
          onClick={() => {
            if (isAvatarTalking) {
              setInterruptAvatar?.(true);
            } else {
              if (showDigitalHuman) {
                setDigitalHumanVoiceMode((pv) => !pv);
              } else {
                setConversationMedium("audio");
              }
            }
          }}
          config={config}
          isAvatarTalking={isAvatarTalking}
        />
      )}
    </div>
  );
});

function DigitalHumanVoiceUI(props: {
  config: WidgetConfig;
  onClose: () => void;
  onInterrupt: () => void;
  interruptAvatar: boolean;
  setInterruptAvatar: Dispatch<SetStateAction<boolean>>;
  brandHSLAString: string;
  deepgramApiKey: string;
  isRecording: boolean;

  setIsRecording: (isRecording: boolean) => void;

  onChange: (text: string) => void;
  onFinal: () => void;
  isAvatarTalking: boolean;
  isThemeSleek: boolean;
}) {
  const {
    config,
    onClose,
    isRecording,
    setIsRecording,
    brandHSLAString,
    deepgramApiKey,
    onChange,
    onFinal,
    setInterruptAvatar,
    isAvatarTalking,
    isThemeSleek,
  } = props;
  const [language, setLanguage] = useLanguage();
  const { isSpeechFinal, frequencyData } = useVoice({
    deepgramApiKey: deepgramApiKey,
    isRecording: isRecording,
    setIsRecording: setIsRecording,
    onChange: onChange,
    language,
    isVoiceBotSpeaking: isAvatarTalking,
  });

  useEffect(() => {
    if (isRecording) {
      isSpeechFinal.current = false;
      return;
    }
    if (isSpeechFinal.current) {
      isSpeechFinal.current = false;
      onFinal();
    }
  }, [isRecording]);

  return (
    <div
      className={cn(
        "flex relative justify-center items-center gap-4 z-20",
        isThemeSleek
          ? "rounded-3xl bg-white/10 backdrop-blur-3xl max-w-44 w-full m-auto"
          : "",
      )}
    >
      <SelectLanguage
        language={language}
        setLanguage={setLanguage}
        supportedLanguages={config.supportedLanguages}
        config={config}
      />
      <Button
        size="icon"
        className="rounded-full p-0 relative z-20"
        variant="secondary"
        onClick={() => {
          setIsRecording(!isRecording);
          if (!isRecording) setInterruptAvatar(true);
        }}
      >
        {isRecording ? (
          <MicIcon className="size-6!" />
        ) : isAvatarTalking ? (
          <SquareIcon className="size-6!" />
        ) : (
          <MicOffIcon className="size-6!" />
        )}
      </Button>
      <AudioVisualizer
        brandHSLAString={brandHSLAString}
        frequencyData={frequencyData}
      />
      <Button
        size="icon"
        className="rounded-full p-0 relative z-20"
        variant="destructive"
        onClick={() => {
          onClose();
          onFinal();
        }}
      >
        <X className="size-6!" />
      </Button>
    </div>
  );
}
